/**
 * Rating prompt — PAID APPS ONLY (iOS + Android).
 *
 * Ratings are the one growth lever a paid app has that costs nothing: they drive store ranking
 * and they are what a parent looks at before paying. So we ask — but only at a moment the player
 * has just done well, and rarely enough that it never becomes nagging:
 *
 *   • after a Survival run of more than `SURVIVAL_MIN` words cleared, or
 *   • after a Pairs run finding at least `PAIRS_MIN` pairs, or
 *   • after a Word Wagon run shipping at least `WAGON_MIN` loads, or
 *   • after a normal round scoring at least `ROUND_MIN`, and
 *   • at most once every `COOLDOWN_DAYS` days, whatever the trigger.
 *
 * Never on the web: a browser player has nothing to rate, and the web build's ask is the buy
 * card instead (see src/game/promo.ts).
 *
 * iOS gets the real OS sheet via StoreKit (the bridge's requestReview). The Android TWA has no
 * JS↔native channel — Play's In-App Review API cannot be reached from inside a Trusted Web
 * Activity — so there it is an explicit, dismissible card that opens the Play listing. That is
 * why Android asks first and iOS does not: Apple's sheet IS the ask, while ours would be a
 * second one, and sending an Android player to the Play Store without warning is a hijack.
 */
import Phaser from 'phaser';
import i18next from 'i18next';
import { platform } from '@/platform';
import { PLAY_STORE_URL } from '@/game/promo';
import { isClipMode } from '@/game/clip';
import { showPromoCard } from '@/game/ui/promoCard';

/** Words cleared in a Survival run that count as "they're enjoying this". */
export const SURVIVAL_MIN = 50;
/**
 * Pairs found in a Diamond Pairs run that counts the same. 20 is past the end of round 3
 * (6 + 8 = 14 clears rounds 1–2), i.e. a run that survived into the 20-card board.
 */
export const PAIRS_MIN = 20;
/**
 * Words found in an Ice Maze run that counts the same. 21 is the exact total of a clean run through
 * the whole ladder (3 + 4 + 4 + 5 + 5), so the ask lands only on a player who reached the
 * diagonals — the rung past the end of the ladder.
 */
export const MAZE_MIN = 21;
/**
 * Loads shipped in a Word Wagon run that counts the same. Six is one rung past the end of the
 * ladder (load 5 is where the seven-lane board and the forced sacrifices start), so the ask lands
 * only on a player who held the top rung together at least once.
 */
export const WAGON_MIN = 6;
/**
 * Rounds cleared in a Word Labyrinth run that counts the same. Four rather than six: a wrong turn
 * costs one of five batteries and the clock costs another, so four cleared boards is already a
 * strong run of the hardest mode (specs/labyrinth-mode.md §5.5).
 */
export const LABYRINTH_MIN = 4;
/** Rocket Read: the adaptive clock meets the child's pace at round 13, so 15 is a real run. */
export const ROCKET_MIN = 15;
/**
 * Dishes served in a Recipe Kitchen run that counts the same. Five: a wrong ingredient and a
 * burnt-out flame each cost one of five corn, so five dishes is a run that mostly read correctly —
 * and the rounds are short, so it is a lower bar than the Labyrinth's four boards, not a higher one.
 */
export const RECIPE_MIN = 5;
/** Score in a normal round that counts the same. */
export const ROUND_MIN = 1000;
/** Minimum gap between two asks. */
export const COOLDOWN_DAYS = 90;

const ASKED_KEY = 'abcs.rating.askedAt.v1';
const DAY_MS = 24 * 60 * 60 * 1000;

export type RatingTrigger =
  | { kind: 'survival'; cleared: number }
  | { kind: 'pairs'; pairs: number }
  | { kind: 'maze'; words: number }
  | { kind: 'labyrinth'; rounds: number }
  | { kind: 'recipe'; dishes: number }
  | { kind: 'rocket'; rounds: number }
  | { kind: 'wagon'; loads: number }
  | { kind: 'round'; score: number };

function lastAskedAt(): number {
  try {
    const n = Number(localStorage.getItem(ASKED_KEY));
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function markAsked(): void {
  try {
    localStorage.setItem(ASKED_KEY, String(Date.now()));
  } catch { /* storage blocked — worst case we ask again next time */ }
}

/** Did this result go well enough to be worth an ask? */
export function ratingEarned(trigger: RatingTrigger): boolean {
  if (trigger.kind === 'survival') return trigger.cleared > SURVIVAL_MIN;
  if (trigger.kind === 'pairs') return trigger.pairs >= PAIRS_MIN;
  if (trigger.kind === 'maze') return trigger.words >= MAZE_MIN;
  if (trigger.kind === 'labyrinth') return trigger.rounds >= LABYRINTH_MIN;
  if (trigger.kind === 'recipe') return trigger.dishes >= RECIPE_MIN;
  if (trigger.kind === 'rocket') return trigger.rounds >= ROCKET_MIN;
  if (trigger.kind === 'wagon') return trigger.loads >= WAGON_MIN;
  return trigger.score >= ROUND_MIN;
}

/**
 * The whole gate, as a pure function so it can be tested without a scene: a good-enough result,
 * and no ask within the cooldown. `lastAsked` of 0 means "never asked".
 *
 * The cooldown compares on absolute distance, so a clock moved backwards (or a device restored
 * from an old backup) can't unlock an early ask by making `now - lastAsked` negative.
 */
export function ratingDue(trigger: RatingTrigger, now: number, lastAsked: number): boolean {
  if (!ratingEarned(trigger)) return false;
  return lastAsked <= 0 || Math.abs(now - lastAsked) >= COOLDOWN_DAYS * DAY_MS;
}

/**
 * Ask for a rating if this result earned it and the cooldown has expired. Safe to call from any
 * end-of-game screen — every gate lives in here.
 */
export function maybeAskForRating(scene: Phaser.Scene, trigger: RatingTrigger): void {
  const bridge = platform();
  if (bridge.platform === 'web') return;
  // The App Clip is not a paid app: there is nothing bought to rate. Guarding here and not just
  // on `requestReview` matters — the clip bridge deliberately omits that method, so without this
  // an App Clip would fall through to the ANDROID branch and offer a Play Store link on iOS.
  if (isClipMode()) return;
  if (!ratingDue(trigger, Date.now(), lastAskedAt())) return;

  markAsked();

  if (bridge.requestReview) {
    // iOS: let the win land on screen first, then hand over to StoreKit's own sheet.
    scene.time.delayedCall(1200, () => bridge.requestReview?.());
    return;
  }

  // Android (TWA): our own card, which opens the Play listing only if the player says yes.
  showPromoCard(scene, {
    title: i18next.t('rate.title'),
    lines: [i18next.t('rate.body')],
    actions: [
      { label: i18next.t('rate.later'), kind: 'ghost' },
      {
        label: i18next.t('rate.cta'),
        kind: 'primary',
        onClick: () => window.open(PLAY_STORE_URL, '_blank', 'noopener'),
      },
    ],
  }, { delayMs: 1200 });
}
